<template>
  <section class="lg:max-w-[600px] lg:mx-auto lg:!px-0">
    <StatusBar v-if="!isDesktopFlow" />
    <section :class="{ 'px-4': !isDesktopFlow }">
      <h1 class="text-2xl md:text-4xl font-bold font-heading">Contact Info</h1>
      <form @submit.prevent class="my-8">
        <div class="flex flex-col space-y-2 xs:flex-row xs:space-y-0">
          <TextInput
            id="fname"
            forInput="fname-input"
            v-model="contact.first_name"
            label="First Name"
            classes="w-full xs:w-1/2 xs:pr-2"
            placeholder="First Name"
          />
          <TextInput
            id="lname"
            forInput="lname-input"
            v-model="contact.last_name"
            label="Last Name"
            classes="w-full xs:w-1/2"
            placeholder="Last Name"
          />
        </div>
        <div class="flex flex-col space-y-2 mt-4 xs:flex-row xs:space-y-0 mb-8">
          <div class="w-full xs:w-3/5 xs:pr-2">
            <TextInput
              id="email"
              forInput="email-input"
              type="email"
              v-model="contact.email"
              label="Email"
              placeholder="Email"
              :inputClasses="`transition duration-200 ease-in-out ${
                emailError ? '!border-[#df1b41] !text-[#df1b41]' : 'border-transparent'
              }`"
              @blur="handleEmailBlur"
              @update:modelValue="handleEmailUpdate"
            />
            <p v-if="emailError" class="text-[#df1b41] text-sm mt-0.5" id="email-error-message">
              {{ emailError }}
            </p>
          </div>
          <div class="w-full xs:w-2/5">
            <TextInput
              id="tel"
              forInput="tel-input"
              type="tel"
              v-model="contact.phone"
              label="Phone"
              placeholder="Phone"
              classes="w-full"
              :inputClasses="`transition duration-200 ease-in-out ${
                phoneError ? '!border-[#df1b41] !text-[#df1b41]' : 'border-transparent'
              }`"
              @blur="handlePhoneBlur"
              @update:modelValue="handlePhoneUpdate"
            />
            <p v-if="phoneError" class="text-[#df1b41] text-sm mt-0.5" id="phone-error-message">
              {{ phoneError }}
            </p>
          </div>
        </div>

        <div
          v-if="error.message"
          id="error-display"
          class="p-2 mt-4 rounded border border-red-400 bg-red-200 text-red-600"
        >
          {{ error.message }}
        </div>
      </form>
      <CheckboxInput
        id="accepts-marketing"
        v-model="contact.accepts_marketing"
        :label="`Keep me informed of upcoming ${settingStore.venue_name} shows`"
        guest
      />
      <p v-show="checkout_text" class="text-sm text-fg-g-primary mt-7">
        {{ checkout_text }}
      </p>
      <div v-if="checkout_confirmation_text" class="mt-7">
        <CheckboxInput
          id="accepts-checkout-confirmation"
          v-model="acceptsCheckoutConfirmation"
          label="I agree to the following policy"
          guest
        />
        <div
          class="text-sm text-fg-g-primary mt-2 whitespace-pre-line"
          id="checkout-confirmation-text"
        >
          {{ checkout_confirmation_text }}
        </div>
      </div>
    </section>
    <CheckoutBottomBar
      @click:next="handleNext"
      :isNextButtonDisabled="!isFormValid || isSaving"
      :nextButtonText="nextButtonText"
    />
  </section>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { z } from 'zod'

import CheckboxInput from '@/common/components/molecules/CheckboxInput.vue'
import TextInput from '@/common/components/molecules/TextInput.vue'
import CheckoutBottomBar from '@/modules/guestUi/components/organisms/CheckoutBottomBar.vue'
import StatusBar from '@/modules/guestUi/components/organisms/StatusBar.vue'
import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'
import { ErrorData, blankError, clearError, displayHandled } from '@/utils/errors'

const settingStore = useSettingStore()
const { checkout_confirmation_text, checkout_text } = storeToRefs(settingStore)

const checkoutStore = useCheckoutStore()
const { hasSufficientGuestInfo, contact, isDesktopFlow, requiresPayment } =
  storeToRefs(checkoutStore)

const acceptsCheckoutConfirmation = ref<boolean>(false)

const isSaving = ref<boolean>(false)
const error = ref<ErrorData>(blankError())

// Email validation
const emailSchema = z.string().email({ message: 'Please enter a valid email address.' })
const emailError = ref<string>('')
const isEmailValid = computed<boolean>(() => emailError.value === '')
function validateEmail(email: string): void {
  const result = emailSchema.safeParse(email)
  if (!result.success) emailError.value = result.error.issues[0].message
  else emailError.value = ''
}
onMounted(handleEmailBlur)
function handleEmailBlur(): void {
  if (contact.value.email.length > 0) validateEmail(contact.value.email)
  else emailError.value = ''
}
function handleEmailUpdate(email: string): void {
  if (email.length === 0) emailError.value = ''
}

// Phone validation
const phoneSchema = z
  .string()
  .max(20, { message: 'Please ensure this field has no more than 20 characters.' })
const phoneError = ref<string>('')
const isPhoneValid = computed<boolean>(() => phoneError.value === '')
function validatePhone(phone: string): void {
  const result = phoneSchema.safeParse(phone)
  if (!result.success) phoneError.value = result.error.issues[0].message
  else phoneError.value = ''
}
onMounted(handlePhoneBlur)
function handlePhoneBlur(): void {
  if (contact.value.phone.length > 0) validatePhone(contact.value.phone)
  else phoneError.value = ''
}
function handlePhoneUpdate(phone: string): void {
  if (phone.length === 0) phoneError.value = ''
}

const isFormValid = computed<boolean>(() => {
  return (
    (!checkout_confirmation_text.value || acceptsCheckoutConfirmation.value) &&
    hasSufficientGuestInfo.value &&
    isEmailValid.value &&
    isPhoneValid.value
  )
})

const nextButtonText = computed<string>(() => {
  if (!requiresPayment.value) {
    return isSaving.value ? 'Reserving...' : 'Reserve'
  }
  return isSaving.value ? 'Saving...' : 'Next'
})

async function handleNext(): Promise<void> {
  if (!isFormValid.value || isSaving.value) return
  isSaving.value = true
  clearError(error.value)
  try {
    await checkoutStore.setContactInfo()
  } catch (e) {
    displayHandled(e, error.value)
  } finally {
    isSaving.value = false
  }
}
</script>
